// src/components/ClientProfile.jsx
import React, { useState, useEffect, useContext } from "react";
import ClientService from "../services/ClientService";
import { AuthContext } from "../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { FaEdit, FaEye } from "react-icons/fa";
import { MdDelete } from "react-icons/md";

const ClientProfile = () => {
    const navigate = useNavigate();
  
  const { clientId } = useParams(); // <-- Get clientId from URL
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    companyName: "",
    address: "",
  });
  const [userInfo, setUserInfo] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    setUserInfo(user);
    console.log("user", user, user?.role);
    console.log("clientId", clientId);

    // Check if user has admin privileges
    const adminCheck = ["ADMIN", "CEO", "VAIBHAV", "EMP"].includes(user?.role);
    setIsAdmin(adminCheck);

    // Fetch client profile data
    const fetchClientData = async () => {
      try {
        let data;

        if (clientId && adminCheck) {
          // Admin viewing a specific client
          data = await ClientService.getClientById(clientId);
        } else {
          // User viewing own profile
          data = await ClientService.getCurrentProfile();
        }
        console.log(data, "data");

        setClientData(data);
        setFormData({
          companyName: data.companyName || "",
          address: data.address || "",
        });
        setLoading(false);
      } catch (err) {
        console.error("Error fetching client data:", err);
        setError("Failed to load client profile.");
        setLoading(false);
      }
    };

    fetchClientData();
  }, [user, clientId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let updatedData;
      const adminCheck = ["ADMIN", "CEO", "VAIBHAV", "EMP"].includes(
        user?.role
      );
      setIsAdmin(adminCheck);
      console.log(isAdmin, "isAdmin");
      console.log(adminCheck, "adminCheck");

      if (clientId && adminCheck) {
        // Admin updating a client
        console.log("Admin updating a client");
        updatedData = await ClientService.updateProfileById(
          clientData._id,
          formData
        );
      } else {
        // User updating own profile
        console.log("User updating own profile");
        updatedData = await ClientService.updateProfile(formData);
      }

      setClientData(updatedData);
      setIsEditing(false);
      setLoading(false);
    } catch (err) {
      console.error("Error updating profile:", err);
      setError("Failed to update profile.");
      setLoading(false);
    }
  };

  const toggleEditMode = () => {
    if (isEditing) {
      // Cancel edit - reset form
      setFormData({
        companyName: clientData.companyName || "",
        address: clientData.address || "",
      });
    }
    setIsEditing(!isEditing);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 text-red-700 p-4 rounded-md my-4" role="alert">
        <p className="font-bold">Error</p>
        <p>{error}</p>
      </div>
    );
  }

  if (!clientData) {
    return (
      <div
        className="bg-yellow-100 text-yellow-700 p-4 rounded-md my-4"
        role="alert"
      >
        <p className="font-bold">No Client Profile Found</p>
        <p>You don't have a client profile yet.</p>
        <button
          onClick={() => ClientService.createProfile(formData)}
          className="mt-4 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
        >
          Create Profile
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg p-6  mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          {clientId && isAdmin ? "Client Profile" : "My Profile"}
        </h1>
        {(userInfo?.role === "CLIENT" || isAdmin) && (
          <button
            onClick={toggleEditMode}
            className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
          >
            {isEditing ? <MdDelete className="text-lg" /> : <FaEdit />}
          </button>
        )}
      </div>

      {isEditing ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="companyName"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Company Name
            </label>
            <input
              type="text"
              id="companyName"
              name="companyName"
              value={formData.companyName}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter company name"
            />
          </div>

          <div>
            <label
              htmlFor="address"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Address
            </label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter address"
            ></textarea>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={toggleEditMode}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 focus:outline-none"
            >
              Save Changes
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-6">
          {/* Client Info Section */}
          <div className="bg-gray-50 p-4 rounded-md">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">
              Company Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Company Name
                </p>
                <p className="text-gray-900 mt-1">
                  {clientData.companyName || "Not specified"}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Address</p>
                <p className="text-gray-900 mt-1">
                  {clientData.address || "Not specified"}
                </p>
              </div>
            </div>
          </div>

          {/* User Info Section */}
          {clientData.user && (
            <div className="bg-gray-50 p-4 rounded-md">
              <h2 className="text-lg font-semibold text-gray-700 mb-4">
                Contact Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Name</p>
                  <p className="text-gray-900 mt-1">{clientData.user.name} </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Email</p>
                  <p className="text-gray-900 mt-1">
                    {clientData.user.email || "Not specified"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Phone</p>
                  <p className="text-gray-900 mt-1">
                    {clientData.user.phone || "Not specified"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Account Status
                  </p>
                  <p className="text-gray-900 mt-1 capitalize">
                    {clientData.user.status || "Not specified"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Projects Section */}
          {/* Projects Section */}
          <div className="bg-gray-50 p-4 rounded-md">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">
              Projects
            </h2>
            {clientData.project && clientData.project.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Project Name
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Status
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Start Date
                      </th>
                      {isAdmin && (
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {clientData.project.map((project, index) => {
                      console.log(project);
                      // Check if project is an object with properties or just an ID string
                      const projectId =
                        typeof project === "object" ? project._id : project;
                      const projectName =
                        typeof project === "object" && project.name
                          ? project.name
                          : `Project ${index + 1}`;
                      const projectStatus =
                        typeof project === "object" && project.status
                          ? project.status
                          : "ACTIVE";

                      return (
                        <tr key={projectId } 
                         onClick={() => navigate(`/dashboard/projects/${project._id}`)
                         }
>
                          <td className="px-6 py-4 whitespace-nowrap bg-gray-50 hover:bg-gray-200">
                            {projectName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                                        ${
                                          projectStatus === "ACTIVE"
                                            ? "bg-green-100 text-green-800"
                                            : projectStatus === "COMPLETED"
                                            ? "bg-blue-100 text-blue-800"
                                            : projectStatus === "PLANNING"
                                            ? "bg-yellow-100 text-yellow-800"
                                            : projectStatus === "PAUSED"
                                            ? "bg-gray-100 text-gray-800"
                                            : projectStatus === "CANCELLED"
                                            ? "bg-red-100 text-red-800"
                                            : "bg-gray-100 text-gray-800"
                                        }`}
                            >
                              {projectStatus}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {typeof project === "object" && project.startDate
                              ? new Date(project.startDate).toLocaleDateString(
                                  "en-US",
                                  {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  }
                                )
                              : new Date().toLocaleDateString()}
                          </td>
                          {isAdmin && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button
                                onClick={() =>
                                  (window.location.href = `/dashboard/projects/${projectId}`)
                                }
                                className="text-blue-600 hover:text-blue-900 ml-4"
                              >
                                <FaEye className="text-lg" />
                              </button>
                              <button
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      "Are you sure you want to remove this project?"
                                    )
                                  ) {
                                    ClientService.removeProject(
                                      clientData._id,
                                      projectId
                                    )
                                      .then(() => {
                                        // Refresh data after removing project
                                        if (clientId) {
                                          ClientService.getClientById(
                                            clientId
                                          ).then(setClientData);
                                        } else {
                                          ClientService.getCurrentProfile().then(
                                            setClientData
                                          );
                                        }
                                      })
                                      .catch((err) => {
                                        console.error(
                                          "Error removing project:",
                                          err
                                        );
                                        setError("Failed to remove project.");
                                      });
                                  }
                                }}
                                className="text-red-600 hover:text-red-900 px-4"
                              >
                                <MdDelete className="text-lg" />
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500">
                No projects associated with this client.
              </p>
            )}
          </div>

          {/* Additional admin actions */}
          {isAdmin && clientId && (
            <div className="flex justify-end mt-6 space-x-4">
              <button
                onClick={() => {
                  if (
                    window.confirm(
                      "Are you sure you want to delete this client profile?"
                    )
                  ) {
                    ClientService.deleteClient(clientData._id)
                      .then(() => {
                        // Redirect to clients list or dashboard
                        window.location.href = "/dashboard/clients";
                      })
                      .catch((err) => {
                        console.error("Error deleting client:", err);
                        setError("Failed to delete client profile.");
                      });
                  }
                }}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none"
              >
                Delete Client
              </button>
              <button
                onClick={() =>
                  (window.location.href = `/dashboard/client/${clientData._id}/add-project`)
                }
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none"
              >
                Add Project
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientProfile;
